import {
  Flex,
  IconButton,
  IconEditLine,
  IconTrashLine,
  List,
  Pill,
  Text,
  ToggleDetails,
  Tooltip,
} from "@instructure/ui";
import { t } from "i18next";
import React, { useContext, useState } from "react";
import { CyclesService } from "../../../services/CyclesService";
import { Card } from "../../Card";
import { ERROR, useNotification } from "../../CustomHooks/NotificationProvider";
import { DialogYesNo } from "../../DialogYesNo/DialogYesNo";
import { CycleContext } from "../CyclesAndGoals";
import { CycleEditModal } from "./CycleEditModal";

const CycleEntry = ({ cycle, usedCycles }) => {
  const { setCycles } = useContext(CycleContext);
  const showNotification = useNotification();
  const [openEdit, setOpenEdit] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  async function handleDeleteCycle(cycle) {
    try {
      await CyclesService.deleteCycle(cycle);
      setCycles((prevCycles) => prevCycles.filter((c) => c.id !== cycle.id));
      setConfirmDelete(false);
      showNotification(
        t("notifications.general.deleteSuccess", {
          obj: t("cyclesAndGoals.cycle"),
        })
      );
    } catch {
      showNotification(
        t("notifications.general.deleteError", {
          obj: t("cyclesAndGoals.cycle"),
        }),
        ERROR
      );
    }
  }

  return (
    <Card
      style={{
        backgroundColor: "#F2F8FA",
        border: "1px solid #ccc",
        borderRadius: "10px",
        padding: "0",
      }}
    >
      <Flex
        justifyItems="space-between"
        alignItems="start"
        wrap="wrap"
        padding="small"
      >
        <Flex.Item margin="0 0 small 0">
          <Text weight="bold">{t("cyclesAndGoals.name")}: </Text>
          {cycle.name}
        </Flex.Item>
        <Flex.Item margin="0 0 0 small">
          <Pill>{`${new Date(
            cycle.start_date
          ).toLocaleDateString()} - ${new Date(
            cycle.end_date
          ).toLocaleDateString()}`}</Pill>
        </Flex.Item>
        <Flex.Item margin="0 0 0 small">
          <IconButton
            renderIcon={IconEditLine}
            margin="0 small 0 0"
            screenReaderLabel={`${t("academicHierarchy.edit")} ${cycle.name}`}
            onClick={() => setOpenEdit(true)}
          />
          <Tooltip
            renderTip={
              usedCycles.includes(cycle.id)
                ? t("cyclesAndGoals.cannotDeleteTooltip")
                : t("cyclesAndGoals.deleteCycleButton")
            }
            placement="top"
            on={["hover"]}
          >
            <IconButton
              renderIcon={IconTrashLine}
              screenReaderLabel={`${t("evidence.delete")} ${cycle.name}`}
              interaction={
                usedCycles.includes(cycle.id) ? "disabled" : "enabled"
              }
              onClick={() => setConfirmDelete(true)}
            />
          </Tooltip>
        </Flex.Item>
        {cycle.type && (
          <Flex.Item size="100%">
            <Text weight="bold">{t("cyclesAndGoals.typeIs")} </Text>
            {cycle.type.name}, {t("cyclesAndGoals.cycleType.durationEvery")}{" "}
            {cycle.type.duration}
          </Flex.Item>
        )}
        <Flex.Item size="100%" margin="small 0 0 0">
          <Text weight="bold">
            {t("cyclesAndGoals.cycleGoal.goalsAndObjectives")}
          </Text>
          {cycle.goals.map((goal) => (
            <ToggleDetails
              key={goal.id}
              size="small"
              summary={
                <Text as="div" size="small" weight="bold">
                  {t("assessment.goal")}:{" "}
                  <Text as="span" weight="normal" size="small">
                    {goal.description}
                  </Text>
                </Text>
              }
            >
              <Text as="div" size="small" weight="bold">
                {t("assessment.objective")}:
              </Text>
              <List size="small">
                {goal.objectives.map((obj) => (
                  <List.Item key={obj.id}>{obj.description}</List.Item>
                ))}
              </List>
            </ToggleDetails>
          ))}
        </Flex.Item>
        {(cycle.parent_cycle || cycle.child_cycles.length > 0) && (
          <Flex.Item size="100%" margin="small 0 0 0">
            <Flex justifyItems="start" alignItems="start" wrap="wrap">
              {cycle.parent_cycle &&
                Object.hasOwn(cycle.parent_cycle, "name") && (
                  <Flex.Item size="50%" padding="0 large 0 0">
                    <span
                      style={{
                        fontSize: "0.9em",
                        color: "#156380",
                        fontWeight: "bold",
                      }}
                    >
                      {t("cyclesAndGoals.childOf")}
                    </span>
                    <List isUnstyled>
                      <List.Item>
                        <span style={{ fontSize: "0.9em", color: "#156380" }}>
                          {cycle.parent_cycle.name}
                        </span>
                      </List.Item>
                    </List>
                  </Flex.Item>
                )}

              {cycle.child_cycles.length > 0 && (
                <Flex.Item>
                  <span
                    style={{
                      fontSize: "0.9em",
                      color: "#156380",
                      fontWeight: "bold",
                    }}
                  >
                    {t("cyclesAndGoals.parentOf")}
                  </span>
                  <List isUnstyled>
                    {cycle.child_cycles.map((child) => (
                      <List.Item key={child.id}>
                        <span style={{ fontSize: "0.9em", color: "#156380" }}>
                          {child.name}
                        </span>
                      </List.Item>
                    ))}
                  </List>
                </Flex.Item>
              )}
            </Flex>
          </Flex.Item>
        )}
      </Flex>
      <CycleEditModal cycle={cycle} open={openEdit} setOpen={setOpenEdit} />
      <DialogYesNo
        message={t("cyclesAndGoals.deleteCycleMessage")}
        openStatus={confirmDelete}
        onClickNo={() => setConfirmDelete(false)}
        onClickYes={() => handleDeleteCycle(cycle)}
      />
    </Card>
  );
};

export default CycleEntry;
